import { FieldConfiguration, FieldEntry, ParentFieldEntry, GetAvailableFieldsResult } from '@aidkitorg/types/lib/admin-panel/settings';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { DataTableRow } from '../AdminPanel/Settings/types';
import { usePost } from '../API';
import { logAdminPanelError } from '../utils/adminPanel.utils';

const SYNTHETIC_UID_COLUMN_NAME = "__synthetic_uid__";

type SettingsProviderProps = {
  children: React.ReactNode
}

export type BulkFieldUpdate = {
  id: string;
  updates: Partial<FieldEntry>;
};

type SettingsContextType = {
  fields: FieldEntry[];
  transformedFields: DataTableRow[];
  getFields: () => void;
  saveField: (id: string, updates: Partial<FieldEntry>) => Promise<void>;
  bulkSaveFields: (updates: BulkFieldUpdate[]) => Promise<void>;
  hasFieldsInitialLoad: boolean;
  isFieldsLoading: boolean;
};

const SettingsContext = createContext<SettingsContextType>({
  fields: [],
  transformedFields: [],
  getFields: async () => {},
  saveField: async () => {},
  bulkSaveFields: async () => {},
  hasFieldsInitialLoad: false,
  isFieldsLoading: false,
});

export const useSettings = () => {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider")
  }

  return context;
}

// Transform fields for DataTable display putting parent fields and their children together
function transformFieldsForDataTable(fields: FieldEntry[]): DataTableRow[] {
  // First pass: build parent-child relationships and index fields
  const parentToChildren = new Map<string, string[]>();
  const fieldById = new Map<string, FieldEntry>();

  fields.forEach(field => {
    fieldById.set(field.id, field);

    if (field.parent) {
      if (!parentToChildren.has(field.parent)) {
        parentToChildren.set(field.parent, []);
      }
      parentToChildren.get(field.parent)!.push(field.id);
    }
  });

  // Convert to DataTable format for all fields, with children inheriting type/category from parent
  return fields
    .map(fieldEntry => {
      const parentEntry = fieldEntry.parent ? fieldById.get(fieldEntry.parent) as ParentFieldEntry | undefined : undefined;
      const configSource = parentEntry || fieldEntry as ParentFieldEntry;

      // Prefer the full list of subsurvey occurrences; fall back to the singular
      // `subsurvey` (older cached data) so the column keeps working during rollout.
      const subsurveys = fieldEntry.subsurveys?.length
        ? fieldEntry.subsurveys
        : fieldEntry.subsurvey
          ? [{ subsurvey: fieldEntry.subsurvey, kind: fieldEntry.kind }]
          : undefined;

      return {
        targetField: fieldEntry.id === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : fieldEntry.id,
        relatedFields: parentToChildren.get(fieldEntry.id) || [],
        parentField: fieldEntry.parent,
        inheritsFromParent: !!fieldEntry.parent && !!parentEntry,
        kind: parentEntry?.kind || fieldEntry.kind,
        category: configSource?.category,
        aiCategory: configSource?.aiCategory,
        autoCategorized: configSource?.autoCategorized,
        displayName: (fieldEntry as ParentFieldEntry).displayName,
        description: (fieldEntry as ParentFieldEntry).description,
        subsurvey: fieldEntry.subsurvey,
        subsurveys,
      };
    })
    .sort((a, b) => a.targetField.localeCompare(b.targetField));
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [fields, setFields] = useState<FieldEntry[]>([]);
  const [hasFieldsInitialLoad, setHasFieldsInitialLoad] = useState<boolean>(false);
  const [isFieldsLoading, setIsFieldsLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const getAvailableFields = usePost("/api/admin_panel/settings/availableFields", { suppressErrorDisplay: true })
  const updateFieldConfigurations = usePost("/api/admin_panel/settings/updateFieldConfigurations", { suppressErrorDisplay: true })

  const fetchFieldsInternal = useCallback(async () => {
    // Prevent multiple simultaneous API calls
    if (isFieldsLoading) {
      return;
    }
    
    setIsFieldsLoading(true);
    const result = await getAvailableFields({ structuredOutput: true })
    setIsFieldsLoading(false);
    setHasFetched(true);

    if (result.success && result.data) {
      const fieldsArray = (result.data as GetAvailableFieldsResult).fields;
      if (Array.isArray(fieldsArray) && fieldsArray.length > 0) {
        if (typeof fieldsArray[0] === 'string') {
          // Simple string array - convert to FieldEntry
          setFields(fieldsArray.map(field => ({ id: field } as FieldEntry)));
        } else {
          // Already FieldEntry objects with hierarchical structure
          setFields(fieldsArray as FieldEntry[]);
        }
      }
      setHasFieldsInitialLoad(true);
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getAvailableFields])

  // Auto-fetch fields once when the provider mounts
  React.useEffect(() => {
    if (!hasFetched && !isFieldsLoading) {
      fetchFieldsInternal();
    }
  }, [hasFetched, isFieldsLoading, fetchFieldsInternal]);

  // Public API for forcing a refresh (e.g., after updates)
  const getFields = useCallback(async () => {
    await fetchFieldsInternal();
  }, [fetchFieldsInternal])


  // Refetches the latest fields from the server to roll back failed optimistic
  // updates. Shared by `saveField` and `bulkSaveFields`.
  const refetchAndReplaceFields = useCallback(async () => {
    const rollbackResult = await getAvailableFields({ structuredOutput: true })
    if (rollbackResult.success && rollbackResult.data) {
      const fieldsArray = (rollbackResult.data as GetAvailableFieldsResult).fields;
      if (Array.isArray(fieldsArray)) {
        if (fieldsArray.length > 0 && typeof fieldsArray[0] === 'string') {
          setFields(fieldsArray.map(field => ({ id: field } as FieldEntry)));
        } else {
          setFields(fieldsArray as FieldEntry[]);
        }
      }
    }
  }, [getAvailableFields])

  const saveField = useCallback(async (id: string, updates: Partial<FieldEntry>) => {
    // optimistic update while preserving order
    setFields(prevFields => prevFields.map(f => f.id === id ? { ...f, ...updates } as FieldEntry : f));

    const config: FieldConfiguration = { id, ...updates }
    const res = await updateFieldConfigurations({ configurations: [config] })
    if (!res.success) {
      await refetchAndReplaceFields();
      // Throw error so InlineEditableText can show error state
      throw new Error(res.message || 'Failed to save field configuration');
    }
  }, [updateFieldConfigurations, refetchAndReplaceFields])

  // Apply the same updates to many fields in a single API call. Used by the
  // bulk-category action on ManageFieldsPage. Updates are applied optimistically
  // and rolled back from the server on failure, matching `saveField`.
  const bulkSaveFields = useCallback(async (updates: BulkFieldUpdate[]) => {
    if (updates.length === 0) return;

    const updatesById = new Map<string, Partial<FieldEntry>>();
    updates.forEach(u => updatesById.set(u.id, u.updates));

    setFields(prevFields => prevFields.map(f => {
      const update = updatesById.get(f.id);
      return update ? { ...f, ...update } as FieldEntry : f;
    }));

    const configurations: FieldConfiguration[] = updates.map(u => ({ id: u.id, ...u.updates }));
    const res = await updateFieldConfigurations({ configurations })
    if (!res.success) {
      await refetchAndReplaceFields();
      throw new Error(res.message || 'Failed to bulk update field configurations');
    }
  }, [updateFieldConfigurations, refetchAndReplaceFields])

  const transformedFields = useMemo(() => {
    return transformFieldsForDataTable(fields);
  }, [fields])

  const value = useMemo(() => ({
    fields,
    transformedFields,
    getFields,
    saveField,
    bulkSaveFields,
    hasFieldsInitialLoad,
    isFieldsLoading
  }), [fields, transformedFields, getFields, saveField, bulkSaveFields, hasFieldsInitialLoad, isFieldsLoading]);
  
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}